/// <amd-module name="Wit/Messages/BoardsMessagesService" />
import * as React from "react";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import * as StringUtil from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, ServiceOptions, Services, VssService } from "VSS/Platform/Context";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { IBoardsMessage } from "./Contracts";

export interface IBoardsMessagesService extends IVssService {
    /**
     * Gets the highest-priority Boards message (if any)
     */
    getMessage(): IReadonlyObservableValue<IBoardsMessage | undefined>;

    /**
     * Show the specified Boards message
     *
     * @param message The message to display
     * @param persistAcrossNextFps If true, persist the message across the next FPS navigation
     */
    showMessage(message: IBoardsMessage, persistAcrossNextFps?: boolean): void;

    /**
     * Dismiss the current Boards message
     */
    dismissMessage(isClosedProgrammatically?: boolean): void;
}

class BoardsMessagesService extends VssService implements IBoardsMessagesService {
    private message = new ObservableValue<IBoardsMessage | undefined>(undefined);
    private persistMessage? = false;

    public _serviceRestart(pageContext: IVssPageContext) {
        super._serviceRestart(pageContext);

        if (!this.persistMessage) {
            this.message.value = undefined;
        }
        this.persistMessage = false;
    }

    public getMessage(): IReadonlyObservableValue<IBoardsMessage | undefined> {
        return this.message;
    }

    public showMessage(message: IBoardsMessage, persistAcrossNextFps?: boolean): void {
        this.message.value = message;
        this.persistMessage = persistAcrossNextFps;
    }

    public dismissMessage(isClosedProgrammatically?: boolean): void {
        if (this.message.value && this.message.value.onDismiss) {
            this.message.value.onDismiss(isClosedProgrammatically);
        }
        this.message.value = undefined;
    }
}

export function getBoardsMessagesService(pageContext: IVssPageContext) {
    return pageContext.getService<IBoardsMessagesService>(serviceName);
}

export function getErrorMessage(error: any, moreDetails?: string[], format?: string, severity?: MessageCardSeverity): IBoardsMessage {
    const formattedMessage = getMessage(error.toString(), format);
    return {
        renderMessage: () => React.createElement("span", undefined, formattedMessage),
        severity: severity ?? MessageCardSeverity.Error,
        moreDetails
    };
}

export function showErrorMessage(pageContext: IVssPageContext, error: any) {
    const errorMessage = getErrorMessage(error);
    const messageService = getBoardsMessagesService(pageContext);
    messageService.showMessage(errorMessage);
}

function getMessage(message: string, format?: string): string {
    if (format) {
        return StringUtil.format(format, message);
    }

    return message;
}

const serviceName = "IBoardsMessagesService";
// Marking this service as persistant since it gives the ability to show messages across FPS boundaries
Services.add(serviceName, { serviceFactory: BoardsMessagesService, options: ServiceOptions.Persistant });
